import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

export default function MatchBoard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(searchParams.get('buyer') || '');
  const [board, setBoard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyerBudget, setBuyerBudget] = useState(null);

  useEffect(() => {
    if (user?.role === 'buyer') {
      setSelectedBuyer(user.buyer_id);
    } else {
      loadBuyers();
    }
  }, []);

  useEffect(() => {
    if (selectedBuyer) loadBoard();
      if (selectedBuyer) loadBuyerBudget(selectedBuyer);
  }, [selectedBuyer]);

  const loadBuyers = async () => {
    try {
      const res = await authFetch(`${API}/api/buyers`);
      if (res.ok) {
        const data = await res.json();
        setBuyers(data);
        if (!selectedBuyer && data.length > 0) {
          setSelectedBuyer(data[0].id);
        }
      }
    } catch (err) {
      console.error('Load buyers error:', err);
    } finally {
      if (!selectedBuyer) setLoading(false);
    }
  };

  const loadBuyerBudget = async (bid) => {
    try {
      const res = await authFetch(`${API}/api/measurables/definitions/${bid}`);
      if (res.ok) {
        const defs = await res.json();
        const budgetDef = defs.find(d => d.name.toLowerCase().includes('budget') || d.name.toLowerCase().includes('max price'));
        setBuyerBudget(budgetDef?.target_value ? parseFloat(budgetDef.target_value) : null);
      }
    } catch (err) { /* silent */ }
  };

  const loadBoard = async () => {
    setLoading(true);
    try {
      const [boardRes, statsRes] = await Promise.all([
        authFetch(`${API}/api/board/buyer/${selectedBuyer}`),
        authFetch(`${API}/api/board/buyer/${selectedBuyer}/stats`),
      ]);
      if (boardRes.ok) setBoard(await boardRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Load board error:', err);
    } finally {
      setLoading(false);
    }
  };

  const listingTypeBadge = (lt) => {
    if (lt === 'for_sale')           return { label: 'FOR SALE',     cls: 'bg-green-900/50 text-green-300 border-green-600/40' };
    if (lt === 'for_sale_and_lease') return { label: 'SALE & LEASE', cls: 'bg-orange-900/50 text-orange-300 border-orange-600/40' };
    if (lt === 'for_lease')          return { label: 'FOR LEASE',    cls: 'bg-blue-900/50 text-blue-300 border-blue-600/40' };
    return null;
  };

  const getTierColor = (tier) => {
    const colors = {
      'ELITE FIT': 'text-[#00E676] bg-green-900/30 border-green-600/30',
      'STRONG FIT': 'text-[#00D4FF] bg-cyan-900/30 border-cyan-600/30',
      'GOOD FIT': 'text-blue-400 bg-blue-900/30 border-blue-600/30',
      'MARGINAL FIT': 'text-yellow-400 bg-yellow-900/30 border-yellow-600/30',
      'POOR FIT': 'text-red-400 bg-red-900/30 border-red-600/30',
    };
    return colors[tier] || 'text-gray-400 bg-gray-900/30 border-gray-600/30';
  };

  const getPriceColor = (price) => {
    if (!buyerBudget || !price) return 'text-[#00E676]'; // Default green
    const p = parseFloat(price);
    const ratio = p / buyerBudget;
    if (ratio <= 1.0) return 'text-[#00E676]';       // At or under budget — green
    if (ratio <= 1.025) return 'text-yellow-300';     // Up to 2.5% over — yellow
    if (ratio <= 1.05) return 'text-yellow-400';      // Up to 5% over — darker yellow
    if (ratio <= 1.075) return 'text-orange-400';     // Up to 7.5% over — orange
    if (ratio <= 1.10) return 'text-orange-500';      // Up to 10% over — darker orange
    return 'text-red-400';                             // Over 10% — red
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${Number(price).toLocaleString()}`;
  };

  const getScorePercent = (score) => {
    if (!score) return 0;
    return Math.round((score / 9) * 100);
  };

  // Color-code the fit percentage on both mobile + desktop cards.
  const fitColorClass = (pct) => {
    if (pct >= 85) return 'text-[#00E676]'; // green
    if (pct >= 70) return 'text-[#00D4FF]'; // cyan
    if (pct >= 55) return 'text-[#FFC107]'; // yellow
    if (pct >= 40) return 'text-[#FF6B35]'; // orange
    return 'text-[#FF5252]';                 // red
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Property Match Board</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">Properties ranked by fit score against client priorities</p>
          </div>
          {user?.role !== 'buyer' && buyers.length > 0 && (
            <select
              value={selectedBuyer}
              onChange={(e) => setSelectedBuyer(e.target.value)}
              className="w-full md:w-auto px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
            >
              <option value="">Select a client...</option>
              {buyers.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
        </div>

        {!selectedBuyer ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <p className="text-[#B0BEC5] text-lg mb-2">Select a client to view their match board</p>
            <p className="text-gray-500">Properties are ranked by how well they match each client's priorities.</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-[#00D4FF] text-lg">Loading match board...</div>
          </div>
        ) : (
          <>
            {/* Stats Bar — hidden on mobile (the property cards themselves carry enough info on small screens). */}
            {stats && stats.total_properties > 0 && (
              <div className="hidden md:grid md:grid-cols-5 gap-4 mb-6">
                <div className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30 p-4 text-center">
                  <div className="text-2xl font-bold text-white">{stats.total_properties}</div>
                  <div className="text-xs text-gray-400">Properties</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-green-600/20 p-4 text-center">
                  <div className="text-2xl font-bold text-[#00E676]">{stats.tier_breakdown?.elite_fit || 0}</div>
                  <div className="text-xs text-gray-400">Elite Fit</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-cyan-600/20 p-4 text-center">
                  <div className="text-2xl font-bold text-[#00D4FF]">{stats.tier_breakdown?.strong_fit || 0}</div>
                  <div className="text-xs text-gray-400">Strong Fit</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-blue-600/20 p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">{stats.tier_breakdown?.good_fit || 0}</div>
                  <div className="text-xs text-gray-400">Good Fit</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30 p-4 text-center">
                  <div className="text-2xl font-bold text-white">{stats.avg_score || 0}</div>
                  <div className="text-xs text-gray-400">Avg Score</div>
                </div>
              </div>
            )}

            {/* Board */}
            {board.length === 0 ? (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
                <p className="text-[#B0BEC5] text-lg mb-2">No evaluated properties yet</p>
                <p className="text-gray-500 mb-4">Grade properties against this client's criteria to see them ranked here.</p>
                <Link to="/properties" className="inline-block px-6 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors">
                  Go to Properties
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {board.map((prop) => {
                  const lt = listingTypeBadge(prop.listing_type);
                  // Abbreviations for the dimension labels on mobile so they don't squish.
                  const dimAbbrev = (name) => ({
                    'Financials': 'Fin',
                    'Zoning & Use': 'Zone',
                    'Building Specs': 'Specs',
                    'Location & Access': 'Loc',
                    'Condition & Systems': 'Cond',
                  }[name] || name);
                  return (
                  <Link
                    key={prop.property_id}
                    to={`/properties/${prop.property_id}/buyer/${selectedBuyer}`}
                    className="block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 hover:border-[#00D4FF]/40 transition-all"
                  >
                    {/* ── MOBILE LAYOUT (compact horizontal: rank+badge · center · fit%) ── */}
                    {(() => { const pct = getScorePercent(prop.fit_score); return (
                    <div className="md:hidden flex items-center gap-3">
                      {/* LEFT: rank + listing-type badge stacked */}
                      <div className="flex flex-col items-center shrink-0 w-12">
                        <div className="text-lg font-bold text-[#546E7A] leading-none">#{prop.rank}</div>
                        {lt && (
                          <span className={`mt-1.5 px-1.5 py-0.5 rounded text-[8px] font-bold border whitespace-nowrap ${lt.cls}`}>
                            {lt.label}
                          </span>
                        )}
                      </div>

                      {/* CENTER: address · city · price */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-sm leading-tight break-words">{prop.address}</div>
                        <div className="text-gray-400 text-xs mt-0.5 truncate">
                          {prop.city}, {prop.state}
                          {prop.sqft ? ` · ${prop.sqft.toLocaleString()} sqft` : ''}
                          {prop.year_built ? ` · ${prop.year_built}` : ''}
                        </div>
                        <div className="mt-1 text-sm font-bold text-[#00E676] leading-tight">
                          {prop.price != null ? formatPrice(prop.price)
                            : prop.lease_rate_sqft != null ? `$${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr`
                            : <span className="text-gray-500">N/A</span>}
                          {prop.price != null && prop.lease_rate_sqft != null && (
                            <span className="text-gray-500 font-normal text-xs ml-1">
                              · ${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr
                            </span>
                          )}
                        </div>
                      </div>

                      {/* RIGHT: fit% (color-coded) + tier */}
                      <div className="text-right shrink-0">
                        <div className={`text-2xl font-bold leading-none ${fitColorClass(pct)}`}>{pct}%</div>
                        <div className="text-[10px] text-gray-400 mt-1 whitespace-nowrap">{prop.tier}</div>
                      </div>
                    </div>
                    ); })()}

                    {/* ── DESKTOP LAYOUT (horizontal cells) ── */}
                    <div className="hidden md:flex md:items-center md:gap-6">
                      <div className="text-center w-12 shrink-0">
                        <div className="text-2xl font-bold text-[#546E7A]">#{prop.rank}</div>
                      </div>
                      <div className="text-center w-20 shrink-0">
                        {(() => { const pct = getScorePercent(prop.fit_score); return (
                          <div className={`text-3xl font-bold ${fitColorClass(pct)}`}>{pct}%</div>
                        ); })()}
                        <div className={`text-xs font-semibold mt-1 px-2 py-0.5 rounded inline-block ${getTierColor(prop.tier)}`}>
                          {prop.tier}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-white font-semibold text-lg">{prop.address}</div>
                          {lt && <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${lt.cls}`}>{lt.label}</span>}
                        </div>
                        <div className="text-gray-400 text-sm">
                          {prop.city}, {prop.state} {prop.zip}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-400">
                          <span>{prop.sqft ? prop.sqft.toLocaleString() : '-'} sqft</span>
                          {prop.lot_size_acres && <span>{prop.lot_size_acres} acres</span>}
                          {prop.year_built && <span>Built {prop.year_built}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {prop.price != null ? (
                          <>
                            <div className={`${getPriceColor(prop.price)} font-bold text-xl`}>{formatPrice(prop.price)}</div>
                            {prop.sqft && (
                              <div className="text-gray-500 text-sm">${Math.round(prop.price / prop.sqft)}/sqft</div>
                            )}
                            {prop.lease_rate_sqft != null && (
                              <div className="text-[#00E676] text-sm mt-1">Lease: ${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                            )}
                          </>
                        ) : prop.lease_rate_sqft != null ? (
                          <div className="text-[#00E676] font-bold text-xl">${Number(prop.lease_rate_sqft).toFixed(2)}/sqft/yr</div>
                        ) : (
                          <div className="text-gray-500 font-bold text-xl">N/A</div>
                        )}
                        {prop.listing_url && (
                          <span
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(prop.listing_url, '_blank'); }}
                            className="inline-block mt-1 text-[#00D4FF] hover:text-[#00E676] text-sm cursor-pointer"
                          >
                            View Listing →
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Dimension Scores: single pipe-separated line on mobile, full grid on desktop */}
                    {prop.dimension_scores && Object.keys(prop.dimension_scores).length > 0 && (
                      <>
                        {/* Mobile: "Fin 5.8 | Zone 6.5 | Specs 6.5 | Loc 6.5 | Cond 6.7" */}
                        <div className="md:hidden mt-2 pt-2 border-t border-[#546E7A]/15 text-[11px] text-gray-500 leading-tight truncate">
                          {Object.entries(prop.dimension_scores).map(([dim, score], i, arr) => (
                            <span key={dim}>
                              {dimAbbrev(dim)} <span className="text-gray-300 font-medium">{Number(score).toFixed(1)}</span>
                              {i < arr.length - 1 && <span className="text-gray-600 mx-1.5">|</span>}
                            </span>
                          ))}
                        </div>

                        {/* Desktop: per-category cells */}
                        <div className="hidden md:flex md:flex-wrap gap-3 mt-3 pt-3 border-t border-[#546E7A]/20">
                          {Object.entries(prop.dimension_scores).map(([dim, score]) => (
                            <div key={dim} className="text-center flex-1 min-w-[60px]">
                              <div className="text-xs text-gray-500">{dim}</div>
                              <div className="text-sm font-semibold text-white">{score}/9</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
